import { Badge } from "@/components/ui/badge";
import { bandPaint } from "../bands";
import type { VersionCardData } from "./api";
import { derivedLabel, personName, timeInStage, versionLabel, words } from "./format";

/** Band as word + colour dot (never colour alone, never coloured text on white). */
export function BandWord({ band }: { band: string }) {
  const paint = bandPaint(band);
  return <span className="inline-flex items-center gap-1 rounded-full border border-ks-grey-3 px-2 py-0.5 text-xs font-medium text-ks-ink"><span aria-hidden className="inline-block size-2 rounded-full" style={{ backgroundColor: paint.solid }} />{band}</span>;
}

export function BandScore({ score }: { score: VersionCardData["score"] }) {
  if (!score) return <span className="text-ks-grey-1">Not rated</span>;
  return <span className="inline-flex items-center gap-2"><span className="font-semibold text-ks-ink">{score.overall.toFixed(1)}</span>{score.band && <BandWord band={score.band} />}</span>;
}

export function VersionCard({ item, onOpen, onKeyDown }: { item: VersionCardData; onOpen: () => void; onKeyDown?: React.KeyboardEventHandler<HTMLLIElement> }) {
  const owner = personName(item.owner_name, item.owner_email);
  const sourceLabel = derivedLabel(item.derived_from);
  return (
    <li role="button" tabIndex={0} data-nonmutating data-card-id={item.version_id} onClick={onOpen} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpen(); } else onKeyDown?.(event); }}
      className="cursor-pointer list-none rounded-lg border border-ks-grey-3 bg-ks-surface p-3 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ks-purple">
      <h3 className="m-0 text-base leading-snug font-semibold text-ks-ink">{item.title}{item.markers.changes_requested ? " ⚑" : ""}{item.markers.foundation_changed ? " ⟳" : ""}</h3>
      <Badge variant="outline" className="mt-2">{versionLabel(item)} {!item.derived_from && !item.locale_of ? "★" : ""}</Badge>
      <p className="mt-2 text-xs text-ks-grey-1">{item.piece_id} · {words(item.content_type)}</p>
      {item.derived_from && <p className="mt-1 text-xs text-ks-grey-1">from {sourceLabel}</p>}
      <div className="mt-2"><BandScore score={item.score} /></div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-ks-grey-1"><span>{owner ?? (item.stage === "approved" ? "—" : <Badge variant="outline">Unassigned</Badge>)}</span><span>{timeInStage(item.time_in_stage_s)}</span></div>
      {item.inherits_approval && <p className="mt-2 text-sm font-medium text-ks-ink">Rides on the English approval</p>}
      {item.inherit_note && <p className="mt-1 text-xs text-ks-grey-1">{item.inherit_note}</p>}
    </li>
  );
}
